import EventEmitter from 'eventemitter3';
import type { CoreAPI } from './api';
import { type Hook, presetHook } from '../hook';
import * as ui from '../ui';
import * as helper from '../helper';
import * as util from '../util';
import type { LifecycleEvent as StandardChartLifecycleEvent } from './MainStandardChartView';
import type { View } from './View';
import { getRendererRegistry, type RendererRegistry } from './renderer';
import type { ThemeConfig } from './theme';

type LifecycleEvent = {
  //
} & StandardChartLifecycleEvent;

type ViewKey =
  | 'NormalView'
  | 'StandardChartView'
  | 'HXKLineView__main'
  | 'ExternalView'
  | 'HXKLineView__secondary';

/**
 * 创建向后兼容的UI对象
 * 直接替换ui对象中的组件为渲染器包装版本
 */
function createUIProxy(registry: RendererRegistry, fallbackUI: typeof ui) {
  // 创建ui对象的浅拷贝
  const enhancedUI = { ...fallbackUI };

  // 获取所有注册的渲染器
  const registeredRenderers = registry.getAll();

  // 替换已注册的渲染器
  registeredRenderers.forEach((renderer, name) => {
    if (name in enhancedUI) {
      // 创建兼容的构造函数包装器
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (enhancedUI as any)[name] = function (container: HTMLElement, options: unknown) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return renderer.create(container, options);
      };
    }
  });

  return enhancedUI;
}

export const buildExtensionApi = ({
  coreAPI,
  hook,
  visId
}: {
  coreAPI: CoreAPI;
  hook: Hook;
  visId: string;
}) => {
  // 使用全局渲染器注册表而不是创建新实例
  const rendererRegistry = getRendererRegistry();

  // 创建向后兼容的ui代理
  const uiProxy = createUIProxy(rendererRegistry, ui);

  // 主题配置（新旧版兼容处理）
  const themeConfig: ThemeConfig | undefined =
    coreAPI.themeConfig ||
    (coreAPI.theme
      ? {
          name: coreAPI.theme,
          token: {}
          // 从旧版theme转换的兼容逻辑，这里可以根据需要添加更多转换
        }
      : undefined);

  return {
    ...coreAPI,

    // 新版主题配置
    themeConfig,

    // internal
    cache: new Map<string, unknown>(),

    views: {} as {
      [key in ViewKey]: View;
    },

    baseDom: {} as { $container: HTMLElement; $mainView: HTMLElement; $secondaryView: HTMLElement },

    // plugin
    hook,
    presetHook,

    // ui: 使用代理对象保证向后兼容
    ui: uiProxy,

    // 渲染器注册表：指向全局实例
    renderers: rendererRegistry,

    // helper
    helper,

    // util
    util,

    // 生命周期函数
    lifecycle: new EventEmitter<LifecycleEvent>(),

    // 唯一标识
    visId
  };
};

export type ExtensionApi = ReturnType<typeof buildExtensionApi>;
